import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';

/**
 * Compte rendu d'une action de masse.
 *
 * S'ouvre TOUJOURS, y compris sur un lot sans échec. Un toast qui s'efface au bout de
 * trois secondes ne prouve rien : après avoir coupé la charge de quarante installations,
 * l'admin doit lire noir sur blanc combien sont passées. Et dès qu'un élément rate, on
 * montre lequel et pourquoi — c'est ce qui manquait à l'ancienne suppression en masse,
 * qui ne disait que « Erreur ».
 *
 * @param {object} report { total, ok, failed: [{ id, error }], simules? }
 * @param {function} resolveLabel (id) => libellé lisible, sinon l'ID brut est affiché
 */
export default function BulkResultDialog({ report, onClose, title = 'Résultat', resolveLabel }) {
  if (!report) return null;
  const failed = report.failed || [];

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-result-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
            <CheckCircle2 className="w-4 h-4" /> {report.ok} réussi{report.ok > 1 ? 's' : ''}
          </span>
          <span className={`flex items-center gap-1.5 font-medium ${failed.length ? 'text-amber-600' : 'text-neutral-400'}`}>
            <AlertTriangle className="w-4 h-4" /> {failed.length} échec{failed.length > 1 ? 's' : ''}
          </span>
          <span className="text-neutral-400 text-xs ml-auto">sur {report.total}</span>
        </div>

        {failed.length > 0 ? (
          <div className="mt-1 max-h-[300px] overflow-y-auto divide-y divide-neutral-100 border border-neutral-200 rounded-md">
            {failed.map((f, i) => (
              <div key={`${f.id || 'n'}-${i}`} className="px-3 py-2" data-testid="bulk-failed-row">
                <p className="text-[12px] font-medium text-neutral-800">
                  {f.id ? (resolveLabel?.(f.id) || f.id) : 'Éléments non traités'}
                </p>
                <p className="text-[11px] text-amber-700">{f.error}</p>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="mt-1 p-3 rounded-md bg-emerald-50 border border-emerald-100 text-[12px] text-emerald-800"
            data-testid="bulk-all-ok"
          >
            Les {report.total} éléments ont accepté l'ordre. Chaque exécution est tracée au
            journal avec votre nom et l'horodatage.
          </div>
        )}

        {report.simules > 0 && (
          <div
            className="flex items-start gap-2 p-3 rounded-md bg-neutral-100 border border-neutral-200 text-[12px] text-neutral-700"
            data-testid="bulk-simulated-count"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500" />
            <span>
              {report.simules} de ces exécutions sont <strong>simulées</strong> : aucune
              intégration n'est branchée, l'équipement n'a rien reçu.
            </span>
          </div>
        )}

        <Button onClick={onClose} className="w-full mt-1" data-testid="bulk-result-close">
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
}
